import { useCallback, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Polyline, CircleMarker, Popup, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';

import { interactiveURL } from '../../../config';
import {
  useAppSelector,
  useAppDispatch,
  currentLayerPathSegmentsSelector,
  currentLayerNodesSelector,
  isEditingSelector,
  editModeSelector,
  selectedEdgeIdSelector,
  selectedNodeIdSelector,
  setSelectedEdgeId,
  setSelectedNodeId,
  removeEdge,
  removeNode,
  updateNode,
  moveNode,
  removeNodeAndReconnect,
  canEditSelector,
  drawingFromNodeIdSelector,
  setDrawingFromNodeId,
  addEdge,
  mapSelector,
  activeImageSelector,
  preferencesSelector,
} from '../../../store';
import type { PathNode } from '../../../store';

import rootElement from '../../../rootElement';
import { QrCodeModal } from './QrCodeModal';

import './PathOverlay.scss';

/**
 * Individual waypoint marker component
 */
interface WaypointMarkerProps {
  node: PathNode;
  isActiveDrawingNode: boolean;
  isDrawingPath: boolean;
  isEditing: boolean;
  isSelected: boolean;
  canEdit: boolean;
  onWaypointClick: (nodeId: string, e: L.LeafletMouseEvent) => void;
  onSelectNode: (nodeId: string) => void;
  onDeleteNode: (nodeId: string) => void;
  onDeleteNodeAndReconnect: (nodeId: string) => void;
  map: L.Map;
  handleMoveNode: (e: L.LeafletMouseEvent) => void;
  movingNode: React.MutableRefObject<L.CircleMarker | null>;
}

function WaypointMarker({
  node,
  isActiveDrawingNode,
  isDrawingPath,
  isEditing,
  isSelected,
  canEdit,
  onWaypointClick,
  onSelectNode,
  onDeleteNode,
  onDeleteNodeAndReconnect,
  map,
  handleMoveNode,
  movingNode,
}: WaypointMarkerProps) {
  const dispatch = useAppDispatch();
  const iMap = useAppSelector(mapSelector);
  const activeImage = useAppSelector(activeImageSelector);
  const [name, setName] = useState(node.name || '');
  const [showQrModal, setShowQrModal] = useState(false);

  // Context menu state (right-click)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  // Nodes with a numeric id are saved, with UUID are not yet saved
  // Prevent QR code generation for unsaved nodes
  const isSavedNode = /^\d+$/.test(node.id);

  const handleSaveFields = useCallback(() => {
    dispatch(updateNode({
      ...node,
      name: name.trim() || undefined,
    }));
  }, [dispatch, node, name]);

  // Generate the waypoint URL for QR code
  const waypointUrl = `${interactiveURL}/${iMap.order.id}/map/${iMap.id}/layer/${activeImage?.id}?from_waypoint=${node.id}`;

  // Close context menu on clicks elsewhere
  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    document.addEventListener('contextmenu', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('contextmenu', handleClick);
    };
  }, [contextMenu]);

  // Named waypoints get special styling (teal), regular get orange
  const hasName = !!node.name;

  let fillColor = '#FF9800'; // default orange
  let strokeColor = '#E65100';
  if (isSelected) {
    fillColor = '#F44336';
    strokeColor = '#B71C1C';
  } else if (isActiveDrawingNode) {
    fillColor = '#2196F3';
    strokeColor = '#1565C0';
  } else if (hasName) {
    fillColor = '#00897B'; // teal for named waypoints
    strokeColor = '#00695C';
  }

  return (
    <>
      <CircleMarker
        center={[node.position.x, node.position.y]}
        radius={isSelected ? 14 : (isDrawingPath ? 10 : (isEditing ? 12 : (hasName ? 8 : 6)))}
        pathOptions={{
          fillColor,
          fillOpacity: 0.9,
          color: strokeColor,
          weight: isSelected ? 4 : (isDrawingPath ? 3 : 2),
          className: isDrawingPath ? 'waypoint-clickable' : 'waypoint-editable',
        }}
        bubblingMouseEvents={false}
        eventHandlers={{
          click: (e) => {
            L.DomEvent.stopPropagation(e);
          },
          contextmenu: (e: L.LeafletMouseEvent) => {
            e.originalEvent.preventDefault();
            e.originalEvent.stopPropagation();
            if (isEditing && canEdit) {
              setContextMenu({ x: e.originalEvent.clientX, y: e.originalEvent.clientY });
            }
          },
          mouseup: (e) => {
            if (!isEditing) return;
            if (isDrawingPath && movingNode.current) {
              dispatch(moveNode({
                nodeId: node.id,
                position: { x: e.latlng.lat, y: e.latlng.lng },
              }));
              movingNode.current = null;
              map.off('mousemove', handleMoveNode);
              if (!map.dragging.enabled()) map.dragging.enable();
            } else {
              L.DomEvent.stopPropagation(e);
              if (isDrawingPath) {
                onWaypointClick(node.id, e);
              } else {
                onSelectNode(node.id);
              }
            }
          },
          mousemove: (e) => {
            if (!isEditing || !isDrawingPath || e.originalEvent.buttons !== 1) return;
            if (!movingNode.current) movingNode.current = e.target;
            map.dragging.disable();
            map.on('mousemove', handleMoveNode);
          },
        }}
      >
        {isEditing && canEdit && !isDrawingPath && (
          <Popup>
            <div className="path-node-popup">
              <p className="waypoint-title">{node.name || 'Waypoint'}</p>
              <div className="name-input">
                <label htmlFor={`name-${node.id}`}>Name:</label>
                <input
                  type="text"
                  id={`name-${node.id}`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={handleSaveFields}
                  placeholder="e.g., Near Elevator B"
                />
              </div>
              <div className="waypoint-actions">
                <button
                  className="qr-code-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowQrModal(true);
                  }}
                  disabled={!isSavedNode}
                  title={
                    isSavedNode
                      ? "Generate QR code for this waypoint"
                      : "Save the waypoint to generate QR code"
                  }
                >
                  📱 QR Code
                </button>
                <button
                  className="delete-node-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteNode(node.id);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </Popup>
        )}
        {/* Show tooltip with name in display mode */}
        {!isEditing && node.name && (
          <Tooltip permanent direction="top" offset={[0, -10]} className="waypoint-name-tooltip">
            {node.name}
          </Tooltip>
        )}
      </CircleMarker>

      {/* Right-click context menu — rendered via portal outside Leaflet DOM to prevent map click events */}
      {contextMenu && createPortal(
        <div
          className="waypoint-context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="waypoint-context-menu__item"
            onClick={() => {
              onDeleteNodeAndReconnect(node.id);
              setContextMenu(null);
            }}
          >
            Delete &amp; Reconnect
          </button>
          <button
            className="waypoint-context-menu__item waypoint-context-menu__item--danger"
            onClick={() => {
              onDeleteNode(node.id);
              setContextMenu(null);
            }}
          >
            Delete
          </button>
        </div>,
        rootElement,
      )}

      {/* QR Code Modal */}
      {showQrModal && (
        <QrCodeModal
          waypointUrl={waypointUrl}
          waypointName={node.name || 'Waypoint'}
          onClose={() => setShowQrModal(false)}
        />
      )}
    </>
  );
}

/**
 * Displays all path edges and nodes on the current map layer.
 * In edit mode, allows selecting and deleting paths.
 * Waypoint nodes (no spaceId) can be clicked to connect paths.
 */
export function PathOverlay() {
  const dispatch = useAppDispatch();
  const map = useMap();
  const segments = useAppSelector(currentLayerPathSegmentsSelector);
  const nodes = useAppSelector(currentLayerNodesSelector);
  const isEditing = useAppSelector(isEditingSelector);
  const editMode = useAppSelector(editModeSelector);
  const selectedEdgeId = useAppSelector(selectedEdgeIdSelector);
  const selectedNodeId = useAppSelector(selectedNodeIdSelector);
  const canEdit = useAppSelector(canEditSelector);
  const drawingFromNodeId = useAppSelector(drawingFromNodeIdSelector);
  const activeImage = useAppSelector(activeImageSelector);
  const preferences = useAppSelector(preferencesSelector);
  const showWaypoints = preferences.showWaypoints ?? true;
  const movingNode = useRef<L.CircleMarker | null>(null);

  // Filter waypoint nodes (nodes without a spaceId)
  const waypointNodes = nodes.filter((n) => !n.spaceId && !n.overlayId);

  // Keyboard handler for Delete key
  useEffect(() => {
    const handleKeyDown = (event: Event) => {
      if (!isEditing || !canEdit) return;

      const e = event as KeyboardEvent;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Don't delete if user is typing in an input
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
          return;
        }

        if (selectedNodeId) {
          dispatch(removeNode(selectedNodeId));
          dispatch(setSelectedNodeId(null));
          e.preventDefault();
        } else if (selectedEdgeId) {
          dispatch(removeEdge(selectedEdgeId));
          dispatch(setSelectedEdgeId(null));
          e.preventDefault();
        }
      }

      // Escape to deselect
      if (e.key === 'Escape') {
        dispatch(setSelectedNodeId(null));
        dispatch(setSelectedEdgeId(null));
      }
    };

    rootElement.addEventListener('keydown', handleKeyDown);
    return () => rootElement.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, isEditing, canEdit, selectedNodeId, selectedEdgeId]);

  const handleEdgeClick = useCallback((edgeId: string) => {
    if (isEditing && canEdit) {
      dispatch(setSelectedEdgeId(selectedEdgeId === edgeId ? null : edgeId));
    }
  }, [dispatch, isEditing, canEdit, selectedEdgeId]);

  const handleDeleteEdge = useCallback((edgeId: string) => {
    dispatch(removeEdge(edgeId));
    dispatch(setSelectedEdgeId(null));
  }, [dispatch]);

  const handleWaypointClick = useCallback((nodeId: string) => {
    // Only handle click for path drawing - let popup open naturally otherwise
    if (editMode !== 'drawingPath' || !activeImage) return;

    if (!drawingFromNodeId) {
      // Start drawing from this waypoint
      dispatch(setDrawingFromNodeId(nodeId));
    } else {
      // Complete the path to this waypoint
      let fromNodeId = drawingFromNodeId;

      // Handle space: prefix
      if (drawingFromNodeId.startsWith('space:')) {
        const spaceId = drawingFromNodeId.replace('space:', '');
        const spaceNode = nodes.find((n) => n.spaceId && String(n.spaceId) === String(spaceId));
        if (spaceNode) {
          fromNodeId = spaceNode.id;
        }
      }

      // Don't connect to self
      if (fromNodeId === nodeId) {
        dispatch(setDrawingFromNodeId(null));
        return;
      }

      // Create the edge
      dispatch(addEdge({
        from: fromNodeId,
        to: nodeId,
        bidirectional: true,
      }));

      // Stop drawing after connecting to second node
      dispatch(setDrawingFromNodeId(null));
    }
  }, [dispatch, editMode, drawingFromNodeId, nodes, activeImage]);

  const handleDeleteNode = useCallback((nodeId: string) => {
    dispatch(removeNode(nodeId));
  }, [dispatch]);

  const handleDeleteNodeAndReconnect = useCallback((nodeId: string) => {
    dispatch(removeNodeAndReconnect(nodeId));
  }, [dispatch]);

  const handleMoveNode = useCallback((e: L.LeafletMouseEvent) => {
    if (!movingNode.current) return;
    movingNode.current.setLatLng(map.mouseEventToLatLng(e.originalEvent));
  }, [map]);

  // Only show path network in edit mode - users see the calculated route via CustomRouteDisplay
  if (!isEditing) {
    return null;
  }

  // Hide waypoint nodes and path lines in edit mode when disabled in settings
  if (!showWaypoints && editMode !== 'drawingPath') {
    return null;
  }

  // Don't show paths if no segments exist and no waypoints
  if (segments.length === 0 && waypointNodes.length === 0) {
    return null;
  }

  return (
    <>
      {/* Render path edges */}
      {segments.map((segment) => {
        const isSelected = selectedEdgeId === segment.edgeId;
        // Prevent clikking edges while drawing new path
        // to avoid conflicts with node selection
        const isEdgeClickable = editMode !== 'drawingPath';

        return (
          <Polyline
            // Add isEdgeClickable to force re-render when toggling drawing mode
            key={`${segment.edgeId}-${isEdgeClickable}`}
            positions={[
              [segment.from.x, segment.from.y],
              [segment.to.x, segment.to.y],
            ]}
            pathOptions={{
              color: isSelected ? '#ff4444' : '#4CAF50',
              weight: isSelected ? 5 : 3,
              opacity: editMode === 'drawingPath' ? 0.5 : 0.8,
              dashArray: isEditing ? '10, 5' : undefined,
              lineCap: 'round',
              lineJoin: 'round',
              interactive: isEdgeClickable,
            }}
            eventHandlers={isEdgeClickable ? {
              click: (e) => {
                e.originalEvent.stopPropagation();
                handleEdgeClick(segment.edgeId);
              },
            } : {}}
          >
            {isEditing && canEdit && isSelected && (
              <Popup>
                <div className="path-edge-popup">
                  <p>Path segment</p>
                  <button
                    className="delete-edge-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteEdge(segment.edgeId);
                    }}
                  >
                    Delete Path
                  </button>
                </div>
              </Popup>
            )}
          </Polyline>
        );
      })}

      {/* Render waypoint nodes - these are clickable for path connections */}
      {waypointNodes.map((node) => {
        const isActiveDrawingNode = drawingFromNodeId === node.id;

        return (
          <WaypointMarker
            key={node.id}
            node={node}
            isActiveDrawingNode={isActiveDrawingNode}
            isDrawingPath={editMode === 'drawingPath'}
            isEditing={isEditing}
            isSelected={selectedNodeId === node.id}
            canEdit={canEdit}
            onWaypointClick={handleWaypointClick}
            onSelectNode={(nodeId) => dispatch(setSelectedNodeId(nodeId))}
            onDeleteNode={handleDeleteNode}
            onDeleteNodeAndReconnect={handleDeleteNodeAndReconnect}
            map={map}
            handleMoveNode={handleMoveNode}
            movingNode={movingNode}
          />
        );
      })}

      {/* Space-linked nodes don't need visible circles - the marker itself represents the node */}
    </>
  );
}
